import { Action, createReducer, on } from '@ngrx/store';
import * as fromActions from '../actions/oring.actions';
import { LookupValue } from 'src/app/models/lookup.model';
import { Loader } from './equipment.reducer';
import { ORing, ORingMaterialName, ORingProductFamily, ORingProductFamilyName, ORingProductSizeValue, ORingProductTypeName, ORingSBU, ORingSBUName, ORingSellable, ORingSellableId, ORingSubSBU, ORingSubSBUName } from 'src/app/models';

export const oringFeatureKey = 'oring';

export enum Loaders {
  loadORings,
  loadORingLookups
}

export interface ORingFilter {
  pipeSizeID?: number;
  materialID?: string;
  productFamilyID?: string;
  sbuID?: string;
  subSbuID?: string;
  productType?: string;
  sellableCodes?: (string | null)[];
  closurePartNumber?: string;
  partNumber?: string;
  tapRequired?: string;
  minTemp?: string;
  maxTemp?: string;
}

export interface State {
  loaders: Loaders[];
  orings: ORing[];
  pipeSizes: ORingProductSizeValue[];
  productTypes: ORingProductTypeName[];
  oringFilter: ORingFilter;
  materials: ORingMaterialName[];
  productFamilies: ORingProductFamilyName[];
  sbus: ORingSBU[];
  subSBUs: ORingSubSBU[];
  sellableStatuses: ORingSellableId[];
  selectedProduct?: string;
}

export const initialState: State = {
  loaders: [],
  orings: [],
  pipeSizes: [],
  oringFilter: {
    sbuID: '-1',
    subSbuID: '-1'
  },
  materials: [],
  productTypes: [],
  productFamilies: [],
  sbus: [],
  subSBUs: [],
  sellableStatuses: []
};

export const reducer = createReducer(
  initialState,

  // the main load for the resultset
  on(fromActions.loadORings, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORings)
  })),
  on(fromActions.loadORingsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORings)
  })),
  on(fromActions.loadORingsSuccess, (state, action) => ({
    ...state,
    orings: action.data,
    loaders: state.loaders.filter(l => l !== Loaders.loadORings)
  })),

  // the selection of a product
  on(fromActions.toggleSelectedProduct, (state, action) => ({
    ...state,
    selectedProduct: state.selectedProduct === action.partNumber ? undefined : action.partNumber
  })),

  // the selection of a product
  on(fromActions.clearSelectedProduct, (state, action) => ({
    ...state,
    selectedProduct: undefined
  })),

  // this provides a way to select the next product in the filter results using the down arrow
  on(fromActions.toggleNextSelectedProduct, (state, action) => {
    let newSelection = undefined;
    const curr = state.orings.findIndex(i => i.partNumber === state.selectedProduct);
    if (state.orings.length > curr + 1) newSelection = state.orings[curr + 1].partNumber;
    return {
      ...state,
      selectedProduct: !!newSelection ? newSelection : state.selectedProduct
    }
  }),

  // this provides a way to select the previous product in the filter results using the up arrow
  on(fromActions.togglePreviousSelectedProduct, (state, action) => {
    let newSelection = undefined;
    const curr = state.orings.findIndex(i => i.partNumber === state.selectedProduct);
    if (curr > 0) newSelection = state.orings[curr - 1].partNumber;

    return {
      ...state,
      selectedProduct: !!newSelection ? newSelection : state.selectedProduct
    }
  }),

  // The update to the actual filter
  on(fromActions.updateORingFilter, (state, action) => {
    return ({
    ...state,
    oringFilter: {
      ...state.oringFilter,
      ...action.obj
    }
  })
}),

  on(fromActions.toggleSellableCode, (state, action) => {
    const codes = (state.oringFilter.sellableCodes || []).includes(action.code) ?
      (state.oringFilter.sellableCodes || []).filter(code => code !== action.code) :
      (state.oringFilter.sellableCodes || []).concat(action.code);

    return {
      ...state,
      oringFilter: {
        ...state.oringFilter,
        sellableCodes: codes.length < 1 ? undefined : codes // I want it to be undefined and not empty
      }
    }
  }),


  // Lookup values ↓↓↓
  on(fromActions.loadMaterials, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadMaterialsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups),
    materials: action.materials
  })),
  on(fromActions.loadMaterialsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups)
  })),
  ///

  on(fromActions.loadPipeSizes, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadPipeSizesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups),
    pipeSizes: action.data
  })),
  on(fromActions.loadPipeSizesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups)
  })),
  ///

  on(fromActions.loadProductTypes, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadProductTypesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups),
    productTypes: action.productTypes
  })),
  on(fromActions.loadProductTypesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups)
  })),
  ///

  on(fromActions.loadProductFamilies, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadProductFamiliesSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadORingLookups),
    productFamilies: action.families
  })),
  on(fromActions.loadProductFamiliesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadORingLookups)
  })),
  ///

  on(fromActions.loadSBUs, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadSBUsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadORingLookups),
    sbus: action.sbus
  })),
  on(fromActions.loadSBUsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(f => f !== Loaders.loadORingLookups)
  })),
  ///

  on(fromActions.loadSubSBUs, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadSubSBUsSuccess, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups),
    subSBUs: action.subSBUs
  })),
  on(fromActions.loadSubSBUsFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups)
  })),
  ///

  on(fromActions.loadSellables, (state, action) => ({
    ...state,
    loaders: state.loaders.concat(Loaders.loadORingLookups)
  })),
  on(fromActions.loadSellablesSuccess, (state, action) => ({
    ...state,
    sellableStatuses: action.sellables,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups)
  })),
  on(fromActions.loadSellablesFail, (state, action) => ({
    ...state,
    loaders: state.loaders.filter(l => l !== Loaders.loadORingLookups)
  }))

);
